/**
 * Figma-aligned PDF styles for monthly_financial_summary exports (jsPDF / autoTable).
 */

export type PdfRgb = [number, number, number];

export function hexToPdfRgb(hex: string): PdfRgb {
  const normalized = hex.replace('#', '').trim();
  const value = parseInt(normalized, 16);
  if (Number.isNaN(value)) {
    throw new Error(`Invalid hex color: ${hex}`);
  }
  return [(value >> 16) & 255, (value >> 8) & 255, value & 255];
}

/** Section header fill colours (Figma Finance-App node 1177-8557). */
export const FINANCIAL_SUMMARY_PDF_HEADER_FILLS = {
  reportSummary: hexToPdfRgb('#003D7A'),
  primarySection: hexToPdfRgb('#0066CC'),
  categoryBreakdown: hexToPdfRgb('#006B7D'),
} as const;

export type FinancialSummaryPdfHeaderSection = keyof typeof FINANCIAL_SUMMARY_PDF_HEADER_FILLS;

export function getFinancialSummarySectionHeaderFillColor(
  section: FinancialSummaryPdfHeaderSection
): PdfRgb {
  return FINANCIAL_SUMMARY_PDF_HEADER_FILLS[section];
}

const WHITE: PdfRgb = [255, 255, 255];

/** Semantic colours (WCAG-oriented; always pair with text labels). */
export const FINANCIAL_SUMMARY_PDF_SEMANTIC = {
  positive: hexToPdfRgb('#16803D'),
  negative: hexToPdfRgb('#B91C1C'),
  warning: hexToPdfRgb('#B45309'),
  warningFill: hexToPdfRgb('#FFFBEB'),
} as const;
const REPORT_SUMMARY_BODY_FILL: PdfRgb = hexToPdfRgb('#E8EFF7');
const REPORT_SUMMARY_BODY_FILL_ALT: PdfRgb = hexToPdfRgb('#EFF6FF');
const BODY_TEXT: PdfRgb = hexToPdfRgb('#333333');
const TABLE_BORDER: PdfRgb = hexToPdfRgb('#E0E0E0');

export function formatFinancialSummaryReportGeneratedDate(
  at: Date = new Date(),
  timeZone = 'Australia/Sydney'
): string {
  return new Intl.DateTimeFormat('en-AU', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone,
  }).format(at);
}

export function financialSummaryReportSummaryTableStyles() {
  return {
    theme: 'plain' as const,
    headStyles: {
      fillColor: FINANCIAL_SUMMARY_PDF_HEADER_FILLS.reportSummary,
      textColor: WHITE,
      fontStyle: 'bold' as const,
      halign: 'left' as const,
      fontSize: 11,
      cellPadding: 4,
    },
    styles: {
      fillColor: REPORT_SUMMARY_BODY_FILL,
      textColor: BODY_TEXT,
      fontSize: 10,
      cellPadding: 3.5,
      lineColor: TABLE_BORDER,
      lineWidth: 0.1,
    },
    alternateRowStyles: {
      fillColor: REPORT_SUMMARY_BODY_FILL_ALT,
    },
    columnStyles: {
      0: { cellWidth: 72, fontStyle: 'normal' as const },
      1: { halign: 'right' as const, fontStyle: 'bold' as const },
    },
  };
}

export function financialSummaryDataNoticeTableStyles() {
  return {
    theme: 'plain' as const,
    headStyles: {
      fillColor: FINANCIAL_SUMMARY_PDF_HEADER_FILLS.primarySection,
      textColor: WHITE,
      fontStyle: 'bold' as const,
      fontSize: 10,
      cellPadding: 4,
      halign: 'left' as const,
    },
    styles: {
      fillColor: FINANCIAL_SUMMARY_PDF_SEMANTIC.warningFill,
      textColor: BODY_TEXT,
      fontSize: 9,
      cellPadding: 3.5,
      lineColor: TABLE_BORDER,
      lineWidth: 0.1,
      overflow: 'linebreak' as const,
      halign: 'left' as const,
      valign: 'top' as const,
    },
    columnStyles: {
      0: { halign: 'left' as const },
    },
  };
}

/** Single full-width column for numbered bullet lists (avoid narrow index column wrapping). */
export function financialSummaryTakeawaysTableStyles() {
  return {
    headStyles: {
      fillColor: FINANCIAL_SUMMARY_PDF_HEADER_FILLS.reportSummary,
      textColor: WHITE,
      fontStyle: 'bold' as const,
      fontSize: 10,
      cellPadding: 4,
    },
    styles: {
      fillColor: WHITE,
      textColor: BODY_TEXT,
      fontSize: 9,
      cellPadding: 3.5,
      lineColor: TABLE_BORDER,
      lineWidth: 0.1,
      overflow: 'linebreak' as const,
    },
    columnStyles: {
      0: { halign: 'left' as const, cellWidth: 'wrap' as const },
    },
  };
}

export function financialSummaryPrimarySectionTableStyles() {
  return {
    headStyles: {
      fillColor: FINANCIAL_SUMMARY_PDF_HEADER_FILLS.primarySection,
      textColor: WHITE,
      fontStyle: 'bold' as const,
      halign: 'left' as const,
      fontSize: 10,
      cellPadding: 4,
    },
    styles: {
      fillColor: WHITE,
      textColor: BODY_TEXT,
      fontSize: 10,
      cellPadding: 3.5,
      lineColor: TABLE_BORDER,
      lineWidth: 0.1,
    },
    columnStyles: {
      0: { halign: 'left' as const },
      1: { halign: 'right' as const },
    },
  };
}

export function financialSummaryPeriodComparisonTableStyles() {
  return {
    headStyles: {
      fillColor: FINANCIAL_SUMMARY_PDF_HEADER_FILLS.primarySection,
      textColor: WHITE,
      fontStyle: 'bold' as const,
      fontSize: 9,
      cellPadding: 3.5,
    },
    styles: {
      fillColor: WHITE,
      textColor: BODY_TEXT,
      fontSize: 9,
      cellPadding: 3,
      lineColor: TABLE_BORDER,
      lineWidth: 0.1,
    },
    columnStyles: {
      0: { halign: 'left' as const },
      1: { halign: 'right' as const },
      2: { halign: 'right' as const },
      3: { halign: 'right' as const },
    },
  };
}

export function financialSummaryCategoryTableStyles() {
  return {
    headStyles: {
      fillColor: FINANCIAL_SUMMARY_PDF_HEADER_FILLS.categoryBreakdown,
      textColor: WHITE,
      fontStyle: 'bold' as const,
      fontSize: 9,
      cellPadding: 3.5,
    },
    styles: {
      fillColor: WHITE,
      textColor: BODY_TEXT,
      fontSize: 9,
      cellPadding: 3,
      lineColor: TABLE_BORDER,
      lineWidth: 0.1,
    },
    columnStyles: {
      0: { halign: 'left' as const },
      1: { halign: 'right' as const },
      2: { halign: 'right' as const },
      3: { halign: 'right' as const },
    },
  };
}

export function financialSummaryBusinessExpenseDetailTableStyles() {
  return withBusinessPdfFooterMargin({
    headStyles: {
      fillColor: FINANCIAL_SUMMARY_PDF_HEADER_FILLS.categoryBreakdown,
      textColor: WHITE,
      fontStyle: 'bold' as const,
      fontSize: 8,
      cellPadding: 3,
    },
    styles: {
      fillColor: WHITE,
      textColor: BODY_TEXT,
      fontSize: 8,
      cellPadding: 2.5,
      lineColor: TABLE_BORDER,
      lineWidth: 0.1,
      overflow: 'linebreak' as const,
    },
    columnStyles: {
      0: { cellWidth: 8, halign: 'right' as const },
      1: { cellWidth: 20, halign: 'left' as const },
      2: { halign: 'left' as const },
      3: { cellWidth: 22, halign: 'left' as const },
      4: { cellWidth: 14, halign: 'right' as const },
      5: { cellWidth: 12, halign: 'center' as const },
      6: { halign: 'right' as const },
    },
  });
}

/** Reserve space at the bottom of each PDF page for disclaimer footer text. */
/** Space reserved above the footer disclaimer on each PDF page (business expense). */
export const REPORT_PDF_FOOTER_RESERVE_MM = 40;

export function withBusinessPdfFooterMargin<T extends Record<string, unknown>>(styles: T): T {
  const existing = (styles.margin as { bottom?: number } | undefined) ?? {};
  return {
    ...styles,
    margin: { ...existing, bottom: REPORT_PDF_FOOTER_RESERVE_MM },
    showHead: 'firstPage' as const,
  };
}

export function financialSummaryBudgetTableStyles() {
  return {
    headStyles: {
      fillColor: FINANCIAL_SUMMARY_PDF_HEADER_FILLS.primarySection,
      textColor: WHITE,
      fontStyle: 'bold' as const,
      fontSize: 8,
      cellPadding: 3,
      overflow: 'linebreak' as const,
    },
    styles: {
      fillColor: WHITE,
      textColor: BODY_TEXT,
      fontSize: 8,
      cellPadding: 2.5,
      lineColor: TABLE_BORDER,
      lineWidth: 0.1,
      overflow: 'linebreak' as const,
    },
    columnStyles: {
      0: { halign: 'left' as const },
      1: { halign: 'right' as const },
      2: { halign: 'right' as const },
      3: { halign: 'right' as const },
    },
  };
}

/** Six-column budget performance layout (Financial Summary Phase A). */
export function financialSummaryBudgetPerformanceTableStyles() {
  return {
    ...financialSummaryBudgetTableStyles(),
    columnStyles: {
      0: { cellWidth: 40, halign: 'left' as const },
      1: { cellWidth: 26, halign: 'right' as const },
      2: { cellWidth: 24, halign: 'right' as const },
      3: { cellWidth: 16, halign: 'right' as const },
      4: { cellWidth: 24, halign: 'left' as const },
      5: { cellWidth: 28, halign: 'right' as const },
    },
  };
}

/** Month-over-month change vs the previous month in the report. */
export const FINANCIAL_SUMMARY_MOM_DELTA_HEADERS = [
  'Income change',
  'Expense change',
  'Net change',
] as const;
